/**
 * device-key.ts — Ed25519 device keypair for Off-Pulse provisional proofs.
 *
 * When the Pulse goes Off-Pulse and the user's device runs a contract
 * locally, the result carries a "provisional proof" — a signature over
 * `{operator_id, inputs, result, zeqondLocal, phaseLocal}` made with the
 * device's private key. On Resync, the server validates the signature
 * against the registered public key, re-runs the operator, and (if the
 * results match) issues a canonical ZeqProof.
 *
 * Web Crypto API has native Ed25519 support in modern browsers (Chrome
 * 113+, Safari 17+, Firefox 130+). No third-party library needed.
 *
 * Key storage:
 *   - Public key  → registered to the user's account (via /api/devices/keys)
 *                   AND mirrored to localStorage so the orb knows its own id.
 *   - Private key → IndexedDB CryptoKey object with `extractable: false`,
 *                   so the key can SIGN but NEVER be read out.
 *                   This is the strongest browser-side key isolation
 *                   available — even an XSS leak can't exfiltrate it.
 *
 * The first time the orb runs in a given browser, we generate a fresh
 * keypair and register the public half. Subsequent loads reuse the
 * stored key.
 */

const KEY_STORE_NAME = "zeq.pulse.device-keys";
const KEY_RECORD_ID = "device-key-v1";
const ALGORITHM = { name: "Ed25519" } as const;

interface KeyRecord {
  id: string;
  publicKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyJwk: JsonWebKey;
  createdAt: number;
}

let cachedRecord: KeyRecord | null = null;

// ─── IndexedDB helpers ─────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open("zeq.pulse", 1);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KEY_STORE_NAME)) {
        db.createObjectStore(KEY_STORE_NAME, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function readStoredKey(): Promise<KeyRecord | null> {
  try {
    const db = await openDb();
    return await new Promise<KeyRecord | null>((resolve, reject) => {
      const tx = db.transaction(KEY_STORE_NAME, "readonly");
      const store = tx.objectStore(KEY_STORE_NAME);
      const req = store.get(KEY_RECORD_ID);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve((req.result as KeyRecord) || null);
    });
  } catch {
    return null;
  }
}

async function writeStoredKey(rec: KeyRecord): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(KEY_STORE_NAME, "readwrite");
    const store = tx.objectStore(KEY_STORE_NAME);
    const req = store.put(rec);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve();
  });
}

// ─── Public API ────────────────────────────────────────────────────────

/**
 * Get-or-create the device keypair. Idempotent across sessions: the same
 * device reuses the same key indefinitely until the user explicitly
 * resets via clearDeviceKey().
 */
export async function getDeviceKey(): Promise<KeyRecord> {
  if (cachedRecord) return cachedRecord;

  const existing = await readStoredKey();
  if (existing && existing.publicKey && existing.privateKey) {
    cachedRecord = existing;
    return existing;
  }

  // Generate fresh keypair. extractable=false on the private half locks
  // it inside the browser's secure key store.
  const kp = (await crypto.subtle.generateKey(
    ALGORITHM,
    /* extractable */ false,
    ["sign", "verify"],
  )) as CryptoKeyPair;

  const publicJwk = await crypto.subtle.exportKey("jwk", kp.publicKey);

  const rec: KeyRecord = {
    id: KEY_RECORD_ID,
    publicKey: kp.publicKey,
    privateKey: kp.privateKey,
    publicKeyJwk: publicJwk,
    createdAt: Date.now(),
  };

  await writeStoredKey(rec);
  cachedRecord = rec;
  return rec;
}

/**
 * Public-key thumbprint — first 16 hex chars of SHA-256(jwk.x). Used as
 * the device id in provisional-proof envelopes.
 */
export async function getDeviceKeyId(): Promise<string> {
  const rec = await getDeviceKey();
  const x = rec.publicKeyJwk.x || "";
  const bytes = new TextEncoder().encode(x);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash).slice(0, 8))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Sign an arbitrary string payload with the device private key. Returns
 * the signature as base64url.
 */
export async function signWithDeviceKey(payload: string): Promise<string> {
  const rec = await getDeviceKey();
  const data = new TextEncoder().encode(payload);
  const sig = await crypto.subtle.sign(ALGORITHM, rec.privateKey, data);
  return base64url(new Uint8Array(sig));
}

/** Public-key JWK — what the server stores so it can verify our signatures. */
export async function getDevicePublicKeyJwk(): Promise<JsonWebKey> {
  const rec = await getDeviceKey();
  return rec.publicKeyJwk;
}

/** Wipe the local key. Forces a fresh keypair on next call. */
export async function clearDeviceKey(): Promise<void> {
  cachedRecord = null;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(KEY_STORE_NAME, "readwrite");
      const store = tx.objectStore(KEY_STORE_NAME);
      const req = store.delete(KEY_RECORD_ID);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });
  } catch { /* noop */ }
}

// ─── Helpers ──────────────────────────────────────────────────────────

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
